import { Injectable } from '@angular/core';
import {
  RecentItem,
  RecentChatbotItem,
  RecentConversationItem,
  RecentTestItem,
  RecentUserItem,
  RecentKnowledgeBaseItem,
  RECENT_ITEMS_KEYS,
} from '../models/recent-items.model';
import { getRecentItems, saveRecentItem } from '../utils/recent-items.util';

@Injectable({
  providedIn: 'root',
})
export class RecentItemsService {
  private readonly MAX_RECENT_ITEMS = 10;
  private readonly MAX_RECENT_BY_TYPE = 6;

  getAllRecentItems(): RecentItem[] {
    return getRecentItems<RecentItem>(RECENT_ITEMS_KEYS.ALL);
  }

  getRecentItemsByType<T extends RecentItem>(
    type: T['type']
  ): T[] {
    const allItems = this.getAllRecentItems();
    return allItems.filter((item) => item.type === type) as T[];
  }

  getRecentChatbots(): RecentChatbotItem[] {
    return getRecentItems<RecentChatbotItem>(RECENT_ITEMS_KEYS.CHATBOTS);
  }

  getRecentConversations(): RecentConversationItem[] {
    return getRecentItems<RecentConversationItem>(
      RECENT_ITEMS_KEYS.CONVERSATIONS
    );
  }

  getRecentTests(): RecentTestItem[] {
    return getRecentItems<RecentTestItem>(RECENT_ITEMS_KEYS.TESTS);
  }

  getRecentUsers(): RecentUserItem[] {
    return getRecentItems<RecentUserItem>(RECENT_ITEMS_KEYS.USERS);
  }

  getRecentKnowledgeBases(): RecentKnowledgeBaseItem[] {
    return getRecentItems<RecentKnowledgeBaseItem>(RECENT_ITEMS_KEYS.KNOWLEDGE_BASES);
  }

  addRecentChatbot(code: string, name: string, route?: string[]): void {
    const item: RecentChatbotItem = {
      type: 'chatbot',
      code,
      name,
      route: route || ['/chatbot/chats', code],
      timestamp: Date.now(),
    };
    this.addRecentItem(item);
    saveRecentItem(RECENT_ITEMS_KEYS.CHATBOTS, item, {
      max: this.MAX_RECENT_BY_TYPE,
      keyFn: (i) => i.code,
    });
  }

  addRecentConversation(
    id: string,
    title: string,
    chatbotCode: string,
    route?: string[]
  ): void {
    const item: RecentConversationItem = {
      type: 'conversation',
      id,
      title,
      chatbotCode,
      route: route || ['/chatbot/conversation-viewer', id],
      timestamp: Date.now(),
    };
    this.addRecentItem(item);
    saveRecentItem(RECENT_ITEMS_KEYS.CONVERSATIONS, item, {
      max: this.MAX_RECENT_BY_TYPE,
      keyFn: (i) => i.id,
    });
  }

  addRecentTest(id: string, name: string, route?: string[]): void {
    const item: RecentTestItem = {
      type: 'test',
      id,
      name,
      route: route || ['/test', id],
      timestamp: Date.now(),
    };
    this.addRecentItem(item);
    saveRecentItem(RECENT_ITEMS_KEYS.TESTS, item, {
      max: this.MAX_RECENT_BY_TYPE,
      keyFn: (i) => i.id,
    });
  }

  addRecentUser(id: string, name: string, email: string, route?: string[]): void {
    const item: RecentUserItem = {
      type: 'user',
      id,
      name,
      email,
      route: route || ['/user', id],
      timestamp: Date.now(),
    };
    this.addRecentItem(item);
    saveRecentItem(RECENT_ITEMS_KEYS.USERS, item, {
      max: this.MAX_RECENT_BY_TYPE,
      keyFn: (i) => i.id,
    });
  }

  addRecentKnowledgeBase(code: string, name: string, route?: string[]): void {
    const item: RecentKnowledgeBaseItem = {
      type: 'knowledge-base',
      code,
      name,
      route: route || ['/knowledge-base', code],
      timestamp: Date.now(),
    };
    this.addRecentItem(item);
    saveRecentItem(RECENT_ITEMS_KEYS.KNOWLEDGE_BASES, item, {
      max: this.MAX_RECENT_BY_TYPE,
      keyFn: (i) => i.code,
    });
  }

  private addRecentItem(item: RecentItem): void {
    saveRecentItem(RECENT_ITEMS_KEYS.ALL, item, {
      max: this.MAX_RECENT_ITEMS,
      keyFn: this.getItemKey,
    });
  }

  private getItemKey(item: RecentItem): string {
    switch (item.type) {
      case 'chatbot':
        return `chatbot:${item.code}`;
      case 'conversation':
        return `conversation:${item.id}`;
      case 'test':
        return `test:${item.id}`;
      case 'user':
        return `user:${item.id}`;
      case 'knowledge-base':
        return `knowledge-base:${item.code}`;
    }
  }

  deleteRecentItem(item: RecentItem): void {
    const itemKey = this.getItemKey(item);
    
    const allItems = this.getAllRecentItems().filter(
      (i) => this.getItemKey(i) !== itemKey
    );
    localStorage.setItem(RECENT_ITEMS_KEYS.ALL, JSON.stringify(allItems));

    const typeKey = this.getTypeKey(item.type);
    if (typeKey) {
      const typeItems = getRecentItems<RecentItem>(typeKey).filter(
        (i) => this.getItemKey(i) !== itemKey
      );
      localStorage.setItem(typeKey, JSON.stringify(typeItems));
    }
  }

  clearRecentItems(type?: RecentItem['type']): void {
    if (!type) {
      localStorage.removeItem(RECENT_ITEMS_KEYS.ALL);
      localStorage.removeItem(RECENT_ITEMS_KEYS.CHATBOTS);
      localStorage.removeItem(RECENT_ITEMS_KEYS.CONVERSATIONS);
      localStorage.removeItem(RECENT_ITEMS_KEYS.TESTS);
      localStorage.removeItem(RECENT_ITEMS_KEYS.USERS);
      localStorage.removeItem(RECENT_ITEMS_KEYS.KNOWLEDGE_BASES);
      return;
    }

    switch (type) {
      case 'chatbot':
        localStorage.removeItem(RECENT_ITEMS_KEYS.CHATBOTS);
        break;
      case 'conversation':
        localStorage.removeItem(RECENT_ITEMS_KEYS.CONVERSATIONS);
        break;
      case 'test':
        localStorage.removeItem(RECENT_ITEMS_KEYS.TESTS);
        break;
      case 'user':
        localStorage.removeItem(RECENT_ITEMS_KEYS.USERS);
        break;
      case 'knowledge-base':
        localStorage.removeItem(RECENT_ITEMS_KEYS.KNOWLEDGE_BASES);
        break;
    }

    const allItems = this.getAllRecentItems().filter(
      (item) => item.type !== type
    );
    localStorage.setItem(RECENT_ITEMS_KEYS.ALL, JSON.stringify(allItems));
  }

  private getTypeKey(type: RecentItem['type']): string | null {
    switch (type) {
      case 'chatbot':
        return RECENT_ITEMS_KEYS.CHATBOTS;
      case 'conversation':
        return RECENT_ITEMS_KEYS.CONVERSATIONS;
      case 'test':
        return RECENT_ITEMS_KEYS.TESTS;
      case 'user':
        return RECENT_ITEMS_KEYS.USERS;
      case 'knowledge-base':
        return RECENT_ITEMS_KEYS.KNOWLEDGE_BASES;
      default:
        return null;
    }
  }
}
